import { GetDoc, GetWSMgr } from "@/Utils/StateLib";
import { DATA_SOURCES_TEST_PREFIX } from "@/appComponents/Blocks/Modals/SettingsModal/ChartSettings/DataSourceConfiguration/Utils";
import { BigInfoIcon, Button, Divider, Flex, isNullish, Select } from "@theorgchartdev/storybook";
import { useTableManagerContext } from "@/providers/AppendTableProvider";
import { getHoverTipContent } from "@/Utils/Misc";
import { appstr } from "@coreapp/utils/appstr";
import { useAppendTableManagement } from "@/appComponents/Blocks/Modals/SettingsModal/ChartSettings/DataSourceConfiguration/DataSources/AppendTable/useAppendTableManagement";
import { useToast } from "@/providers/ToastProvider";
import { useTopic } from "@/hooks/useTopic";
import { createBoldToastMessage } from "@/appComponents/Blocks/Cards/ToastContainer/utils";

export const GetTableKey = config => {
  return config["source"] === "Append Plan" ? "plans" : "appendtables";
};
export const GetCurrentTableName = config => {
  const tableKey = GetTableKey(config);
  let curTableName = null;
  const wsMgr = GetWSMgr();
  if (config && config["override"]) {
    var tableID = config["override"]["tableID"];
    var curTable = tableKey === "plans" ? "Plan ID" : wsMgr.fOrgInfo.GetAppendTableByID(tableID);
    if (curTable != null) curTableName = curTable["name"];
    else curTableName = appstr("Entity not found", { entity: appstr("Table") });
  }
  return curTableName;
};

export const AppendTableSetupComposite = ({ config, onChange, chartConfig }) => {
  const wsMgr = GetWSMgr();
  const doc = GetDoc();
  const existingTableData = wsMgr.fOrgInfo.GetAppendTableList();
  const noExistingTables =
    !existingTableData || (existingTableData && existingTableData.length === 0);

  const currentTableName = GetCurrentTableName(config) ?? appstr("Select a table");
  const { setShowTableManager, setShowCreateAppendTableModal } = useTableManagerContext();

  const { addToast } = useToast();

  const { openTable } = useAppendTableManagement(wsMgr);

  useTopic("Auto Set Default Table", firstTable => {
    const selectedTable = wsMgr.fOrgInfo.GetAppendTableByID(firstTable?.ID);
    if (selectedTable == null) return;
    handleTableChange(selectedTable);
    addToast({
      ...createBoldToastMessage("Active table set", {
        tableName: firstTable?.name
      }),
      status: "info",
      timeoutMS: 5000
    });
  });

  const handleEditTable = async () => {
    const tableID = config["override"]["tableID"];
    await openTable(tableID, chartConfig?.ID);
  };

  const openTableManager = () => {
    setShowTableManager(true);
  };

  const createNewTable = () => {
    setShowCreateAppendTableModal(true);
  };

  const onSelectTable = option => {
    const tableID = option.value;
    const selectedTable = wsMgr.fOrgInfo.GetAppendTableByID(tableID);
    if (selectedTable == null) return;
    handleTableChange(selectedTable);
  };

  const handleTableChange = targetTable => {
    let appendTableRefObj = {
      name: targetTable["name"],
      collection: targetTable["table"],
      tableID: targetTable["ID"]
    };
    onChange({ ...config, override: appendTableRefObj });
  };

  const isValidTableSelection = () => {
    const appendTableConfig = config?.override;
    return !isNullish(wsMgr?.fOrgInfo?.GetAppendTableByID(appendTableConfig?.tableID));
  };

  const BuildTableOptions = () => {
    let tableOptions = [];
    if (!existingTableData) return tableOptions;
    for (let table of existingTableData) {
      let tableID = table.ID;
      let name = table.name;
      let option = {};
      option.label = name;
      option.value = tableID;
      option.clickHandler = onSelectTable;
      tableOptions.push(option);
    }
    return tableOptions;
  };

  const dataTestIdPrefix = "append-table";
  const newDataTestIdPrefix = `${DATA_SOURCES_TEST_PREFIX}-append-table`;
  const appendTableConfig = config?.override;
  const canEditSelectedTable = appendTableConfig != null && doc != null && isValidTableSelection();

  return (
    <Flex direction={"column"} height={"100%"} gap={"12px"}>
      <Flex direction={"column"} alignItems={"center"} gap={"12px"}>
        <BigInfoIcon name="table" color="brand" />
        <Flex height={"100%"} gap={"8px"}>
          <Button
            modifier={"secondary"}
            data-testid={`${dataTestIdPrefix}-create-table`}
            onClick={createNewTable}
          >
            {appstr("Create Table")}
          </Button>
          <Button
            modifier={"primary"}
            data-testid={`${dataTestIdPrefix}-open-manager`}
            onClick={openTableManager}
            tooltipProps={getHoverTipContent("View Tables")}
            disabled={noExistingTables}
          >
            {appstr("Manage Tables")}
          </Button>
        </Flex>
      </Flex>
      <Divider vertical={false} />
      <Flex gap={"12px"} width={"100%"} alignItems={"center"} flex={"1"}>
        <Flex alignItems={"flex-end"} width={"100%"} justifyContent={"space-between"} gap={"12px"}>
          <div style={{ width: "100%" }}>
            <Select
              fillWidth
              dataTestId={`${dataTestIdPrefix}-select`}
              label={appstr("Active append table")}
              selectSize={"medium"}
              placeholder={currentTableName}
              value={appendTableConfig?.tableID}
              options={BuildTableOptions()}
              inputOnly={false}
              disabled={noExistingTables}
            />
          </div>
          <Button
            data-testid={`${newDataTestIdPrefix}-edit-table`}
            modifier={"secondary"}
            disabled={!canEditSelectedTable}
            onClick={handleEditTable}
            tooltipProps={{
              ...getHoverTipContent("Edit Selected Table"),
              position: "bottom-start"
            }}
          >
            {appstr("Edit Table")}
          </Button>
        </Flex>
      </Flex>
    </Flex>
  );
};
